// Maps the purpose-built pricing editors (purge ranges, depressurization matrix, named values) to and from the
// stored logic expression. The expression is the single source of truth; each editor writes one canonical
// shape and recognizes only that shape. Anything else is reported as unrecognized and shown read-only.

export const LIQUID_CONDITIONAL_CHARGE = 'Billing.Liquid.ConditionalCharge';
export const LIQUID_NAMED_VALUE = 'Pricing.Liquid.NamedValue';
export const LIQUID_CUSTOMER_OWNER = 'LiquidCustomer';
export const BILLING_LINE = 'BillingLine';

export const FACTS = {
  purgePpm: 'purity.beforeFill.totalPpm',
  pressure: 'arrival.hePressure.psig',
  initialTemp: 'filling.initialTemp.k',
} as const;

export const LINE_CODES = { purge: 'PF', depressurization: 'MP' } as const;

export const EDITOR_HINTS = { purge: 'purge', matrix: 'matrix', value: 'value' } as const;
export type EditorKind = keyof typeof EDITOR_HINTS;

export const PURPOSE_BY_KIND: Record<EditorKind, string> = {
  purge: LIQUID_CONDITIONAL_CHARGE,
  matrix: LIQUID_CONDITIONAL_CHARGE,
  value: LIQUID_NAMED_VALUE,
};

// Administration role Pricing Edit = 36. While conditional pricing is being built, the UI is shown only to users who
// hold this role explicitly; Application Admin alone does not reveal it.
export const PRICING_EDIT_ROLE_ID = 36;

export const hasPricingEditRole = (roles: { roleId: number }[] | null | undefined) =>
  !!roles?.some(r => r.roleId === PRICING_EDIT_ROLE_ID);

export type LogicNode = { op: string; [key: string]: any };
export interface LogicExpression { schemaVersion: 1; root: LogicNode }

/** A fee is an amount, "no charge" (null), or a reference to a published named value. */
export type Fee = number | null | { name: string };

/**
 * Which range a reading exactly on a limit falls into, chosen per limit.
 * - above: the limit starts the next range (… < 40 | 40 ≤ …), as the purge contract reads ("40 - 200" includes 40)
 * - below: the limit ends the range (… ≤ 30 | 30 < …), as the depressurization contract reads (">20 but <=30")
 * Because one side always includes the limit and the other excludes it, ranges never gap or overlap.
 */
export type LimitSide = 'above' | 'below';

/** Ascending limits splitting an axis into limits.length + 1 ranges; sides[i] applies to limits[i]. */
export interface Axis { limits: number[]; sides: LimitSide[] }

export const uniformAxis = (limits: number[], side: LimitSide): Axis => ({ limits, sides: limits.map(() => side) });

export interface PurgeModel {
  axis: Axis;
  /** One per range (limits.length + 1). */
  fees: Fee[];
  description: string;
}

export interface MatrixModel {
  pressure: Axis;
  temperature: Axis;
  /** fees[row][column]; rows are pressure ranges, columns are temperature ranges. */
  fees: Fee[][];
  description: string;
}

export type NamedValueType = 'Number' | 'Boolean' | 'String' | 'Date';

/** Publishes one named value with a fixed value. Dates are yyyy-MM-dd strings. */
export interface NamedValueModel { name: string; type: NamedValueType; value: number | boolean | string }

interface Band { lo: number | null; hi: number | null; loInclusive: boolean; hiInclusive: boolean }

export const bands = ({ limits, sides }: Axis): Band[] =>
  Array.from({ length: limits.length + 1 }, (_, i) => {
    const lo = i === 0 ? null : limits[i - 1];
    const hi = i === limits.length ? null : limits[i];
    return {
      lo,
      hi,
      loInclusive: lo !== null && sides[i - 1] === 'above',
      hiInclusive: hi !== null && sides[i] === 'below',
    };
  });

const fact = (key: string): LogicNode => ({ op: 'fact', key });

const between = (key: string, band: Band): LogicNode => ({
  op: 'between', value: fact(key), lo: band.lo, hi: band.hi, loInclusive: band.loInclusive, hiInclusive: band.hiInclusive,
});

const feeNode = (code: string, description: string, fee: Fee): LogicNode => fee === null
  ? { op: 'none' }
  : {
    op: 'emit',
    type: BILLING_LINE,
    fields: {
      code: { op: 'const', value: code },
      description: { op: 'const', value: description },
      quantity: { op: 'const', value: 1 },
      unitPrice: typeof fee === 'number' ? { op: 'const', value: fee } : { op: 'param', name: fee.name },
    },
  };

export const purgeToExpression = (model: PurgeModel): LogicExpression => ({
  schemaVersion: 1,
  root: {
    op: 'unique',
    cases: bands(model.axis).map((band, i) => ({
      when: between(FACTS.purgePpm, band),
      then: feeNode(LINE_CODES.purge, model.description, model.fees[i]),
    })),
    else: { op: 'none' },
  },
});

export const matrixToExpression = (model: MatrixModel): LogicExpression => {
  const rows = bands(model.pressure);
  const columns = bands(model.temperature);
  return {
    schemaVersion: 1,
    root: {
      op: 'unique',
      cases: rows.flatMap((row, r) => columns.map((column, c) => ({
        when: { op: 'and', args: [between(FACTS.pressure, row), between(FACTS.initialTemp, column)] },
        then: feeNode(LINE_CODES.depressurization, model.description, model.fees[r][c]),
      }))),
      else: { op: 'none' },
    },
  };
};

const isNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const isConst = (node: LogicNode | undefined, test: (value: unknown) => boolean) => node?.op === 'const' && test(node.value);
const sameBand = (a: Band, b: Band) =>
  a.lo === b.lo && a.hi === b.hi && a.loInclusive === b.loInclusive && a.hiInclusive === b.hiInclusive;

const readBetween = (node: LogicNode, key: string): Band | null => {
  if (node?.op !== 'between' || node.value?.op !== 'fact' || node.value.key !== key) return null;
  const { lo, hi, loInclusive, hiInclusive } = node;
  if ((lo !== null && !isNumber(lo)) || (hi !== null && !isNumber(hi))) return null;
  if (typeof loInclusive !== 'boolean' || typeof hiInclusive !== 'boolean') return null;
  return { lo, hi, loInclusive, hiInclusive };
};

/**
 * Recovers an axis from contiguous bands; null unless the bands are one open-ended sequence in which each limit
 * is included on exactly one side (no gaps, no overlaps).
 */
const readAxis = (sequence: Band[]): Axis | null => {
  if (sequence.length < 2 || sequence[0].lo !== null || sequence[sequence.length - 1].hi !== null) return null;
  if (sequence[0].loInclusive || sequence[sequence.length - 1].hiInclusive) return null;
  const limits: number[] = [];
  const sides: LimitSide[] = [];
  for (let i = 0; i < sequence.length - 1; i++) {
    const { hi, hiInclusive } = sequence[i];
    const { lo, loInclusive } = sequence[i + 1];
    if (hi === null || hi !== lo || hiInclusive === loInclusive) return null;
    if (i > 0 && hi <= limits[i - 1]) return null;
    limits.push(hi);
    sides.push(hiInclusive ? 'below' : 'above');
  }
  return { limits, sides };
};

/** Reads a "no charge" (none) or a single fee line with the expected code; undefined if neither. */
const readFee = (node: LogicNode, code: string): { fee: Fee; description?: string } | undefined => {
  if (node?.op === 'none') return { fee: null };
  if (node?.op !== 'emit' || node.type !== BILLING_LINE) return undefined;
  const fields = node.fields ?? {};
  if (Object.keys(fields).sort().join() !== 'code,description,quantity,unitPrice') return undefined;
  if (!isConst(fields.code, v => v === code) || !isConst(fields.quantity, v => v === 1)) return undefined;
  if (!isConst(fields.description, v => typeof v === 'string')) return undefined;
  const price = fields.unitPrice;
  if (isConst(price, isNumber)) return { fee: price.value, description: fields.description.value };
  if (price?.op === 'param' && typeof price.name === 'string' && Object.keys(price).length === 2)
    return { fee: { name: price.name }, description: fields.description.value };
  return undefined;
};

const isCanonicalRoot = (root: LogicNode) =>
  root?.op === 'unique' && Array.isArray(root.cases) && root.else?.op === 'none';

const sharedDescription = (fees: { description?: string }[], fallback: string): string | null => {
  const descriptions = Array.from(new Set(fees.map(f => f.description).filter((d): d is string => d !== undefined)));
  if (descriptions.length > 1) return null;
  return descriptions[0] ?? fallback;
};

export const recognizePurge = (expression: LogicExpression | null | undefined): PurgeModel | null => {
  const root = expression?.root;
  if (expression?.schemaVersion !== 1 || !root || !isCanonicalRoot(root)) return null;
  const sequence: Band[] = [];
  const fees: { fee: Fee; description?: string }[] = [];
  for (const c of root.cases) {
    const band = readBetween(c?.when, FACTS.purgePpm);
    const fee = readFee(c?.then, LINE_CODES.purge);
    if (!band || !fee) return null;
    sequence.push(band);
    fees.push(fee);
  }
  const axis = readAxis(sequence);
  const description = sharedDescription(fees, 'Purge Fee');
  if (!axis || description === null) return null;
  return { axis, fees: fees.map(f => f.fee), description };
};

export const recognizeMatrix = (expression: LogicExpression | null | undefined): MatrixModel | null => {
  const root = expression?.root;
  if (expression?.schemaVersion !== 1 || !root || !isCanonicalRoot(root)) return null;
  const cells: { pressure: Band; temperature: Band; fee: Fee; description?: string }[] = [];
  for (const c of root.cases) {
    if (c?.when?.op !== 'and' || !Array.isArray(c.when.args) || c.when.args.length !== 2) return null;
    const pressure = readBetween(c.when.args[0], FACTS.pressure);
    const temperature = readBetween(c.when.args[1], FACTS.initialTemp);
    const fee = readFee(c.then, LINE_CODES.depressurization);
    if (!pressure || !temperature || !fee) return null;
    cells.push({ pressure, temperature, ...fee });
  }

  const distinct = (items: Band[]) => items.filter((b, i) => items.findIndex(o => sameBand(o, b)) === i);
  const rowBands = distinct(cells.map(c => c.pressure));
  const columnBands = distinct(cells.map(c => c.temperature));
  const pressure = readAxis(rowBands);
  const temperature = readAxis(columnBands);
  const description = sharedDescription(cells, 'Depressurization Fee');
  if (!pressure || !temperature || description === null || cells.length !== rowBands.length * columnBands.length) return null;

  // Cells must be stored row by row (pressure), then column by column (temperature).
  const fees: Fee[][] = [];
  for (let r = 0; r < rowBands.length; r++) {
    fees.push([]);
    for (let col = 0; col < columnBands.length; col++) {
      const cell = cells[r * columnBands.length + col];
      if (!sameBand(cell.pressure, rowBands[r]) || !sameBand(cell.temperature, columnBands[col])) return null;
      fees[r].push(cell.fee);
    }
  }
  return { pressure, temperature, fees, description };
};

export const namedValueToExpression = (model: NamedValueModel): LogicExpression => ({
  schemaVersion: 1,
  root: {
    op: 'publish',
    name: model.name,
    value: model.type === 'Date' ? { op: 'const', value: model.value, type: 'date' } : { op: 'const', value: model.value },
  },
});

export const recognizeNamedValue = (expression: LogicExpression | null | undefined): NamedValueModel | null => {
  const root = expression?.root;
  if (expression?.schemaVersion !== 1 || root?.op !== 'publish' || typeof root.name !== 'string') return null;
  const value = root.value;
  if (value?.op !== 'const') return null;
  if (value.type === 'date') return typeof value.value === 'string' ? { name: root.name, type: 'Date', value: value.value } : null;
  if (isNumber(value.value)) return { name: root.name, type: 'Number', value: value.value };
  if (typeof value.value === 'boolean') return { name: root.name, type: 'Boolean', value: value.value };
  if (typeof value.value === 'string') return { name: root.name, type: 'String', value: value.value };
  return null;
};

export interface Recognized { kind: EditorKind; purge?: PurgeModel; matrix?: MatrixModel; value?: NamedValueModel }

export const recognize = (expression: LogicExpression | null | undefined, editorHint?: string | null): Recognized | null => {
  const kinds: EditorKind[] = ['purge', 'matrix', 'value'];
  const order = editorHint && (kinds as string[]).includes(editorHint)
    ? [editorHint as EditorKind, ...kinds.filter(k => k !== editorHint)]
    : kinds;
  for (const kind of order) {
    if (kind === 'purge') {
      const purge = recognizePurge(expression);
      if (purge) return { kind, purge };
    } else if (kind === 'matrix') {
      const matrix = recognizeMatrix(expression);
      if (matrix) return { kind, matrix };
    } else {
      const value = recognizeNamedValue(expression);
      if (value) return { kind, value };
    }
  }
  return null;
};

export const formatNamedValue = (model: NamedValueModel) =>
  model.type === 'Boolean' ? (model.value ? 'Yes' : 'No') : String(model.value);

export const summarize = (expression: LogicExpression, editorHint?: string | null): { type: string; summary: string } => {
  const recognized = recognize(expression, editorHint);
  if (recognized?.purge) return { type: 'Purge Fee', summary: `${recognized.purge.fees.length} ranges` };
  if (recognized?.matrix) {
    const { fees } = recognized.matrix;
    return { type: 'Depressurization Fee', summary: `${fees.length} × ${fees[0]?.length ?? 0} matrix` };
  }
  if (recognized?.value) return { type: 'Named Value', summary: `${recognized.value.name} = ${formatNamedValue(recognized.value)}` };
  return { type: 'Custom', summary: 'Custom expression' };
};

/** Problems that would make an axis unsaveable; empty when valid. */
export const axisProblems = (limits: (number | null)[], label: string): string[] => {
  if (limits.length === 0) return [`${label} needs at least two ranges.`];
  if (limits.some(l => l === null || !Number.isFinite(l))) return [`Every ${label.toLowerCase()} limit needs a number.`];
  if (limits.some((l, i) => i > 0 && (l as number) <= (limits[i - 1] as number)))
    return [`${label} limits must increase from one range to the next.`];
  return [];
};

/** Fee text is blank (no charge), an amount, or @name for a published named value. */
export const parseFee = (text: string): Fee => {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  if (trimmed.startsWith('@')) return { name: trimmed.substring(1) };
  return Number(trimmed);
};

/**
 * Problems with fees; empty when valid. <paramref name="numberNames"/> are the declared Number named values a fee
 * may reference.
 */
export const feeProblems = (fees: Fee[], numberNames: string[] = []): string[] => {
  const problems: string[] = [];
  if (fees.some(f => typeof f === 'number' && (!Number.isFinite(f) || f < 0)))
    problems.push('Fees must be blank (no charge), an amount of 0 or more, or @name of a named value.');
  const unknown = Array.from(new Set(fees.filter((f): f is { name: string } => typeof f === 'object' && f !== null)
    .map(f => f.name).filter(n => !numberNames.includes(n))));
  if (unknown.length) problems.push(`Unknown named value${unknown.length > 1 ? 's' : ''}: ${unknown.map(n => `@${n}`).join(', ')}.`);
  return problems;
};
